import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { onError } from '@apollo/client/link/error';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { Router } from '@angular/router';
import { Alert } from 'src/app/modules/alert/components/model/alert.model';
import { ErrorCodes, ErrorTypes } from 'src/app/services/apollo-setup/types/graphql-exception.enum';
import { HttpLink } from 'apollo-angular/http';
import { environment } from 'src/environments/environment';
import { HeadersService } from '../showtime-api/headers.service';
import { AlertService } from '../../modules/alert/components/services/alert.service';
import { GraphQLError } from '../apollo/graphql-payload.type';
import { MetadataService } from '../metadata/metadata.service';
import { MatDialog } from '@angular/material/dialog';
import { NotSupportedComponent } from 'src/app/components/dialogs/not-supported/not-supported.component';
import { take } from 'rxjs';
import { LocalStorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class ApolloSetupService {
  private isNotSupportedModalOpen: boolean = false;

  constructor(
    private apollo: Apollo,
    private headersService: HeadersService,
    private messageService: AlertService,
    private router: Router,
    private httpLink: HttpLink,
    private metadata: MetadataService,
    private dialog: MatDialog,
    private localStorageService: LocalStorageService,
  ) {}

  setupApolloClient(): void {
    const rmdHeadersMiddleware = new ApolloLink((operation, forward) => {
      const headers = this.headersService.getRmdHeadersForApollo();
      operation.setContext({
        headers,
      });
      return forward(operation);
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        this.handleGraphQlErrors(graphQLErrors as unknown as GraphQLError[]);
      }
      if (networkError) {
        this.showErrorMessage();
      }
    });

    const apolloLinks = ApolloLink.from([
      errorLink,
      rmdHeadersMiddleware,
      this.httpLink.create({ uri: this.metadata.instanceInfo.appSyncUrl }),
    ]);

    this.apollo.create({
      link: apolloLinks,
      cache: new InMemoryCache({
        typePolicies: {
          PatientWrapper: { keyFields: ['patient', ['id']] },
        },
      }),
    });

    this.apollo.client.defaultOptions = {
      mutate: {
        errorPolicy: 'all',
      },
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
    };
  }

  showErrorMessage(): void {
    const errorMessage = 'ERRORS.ApplicationError.ApplicationErrorOccured';
    const errorOptions: Partial<Alert> = {
      links: [
        {
          title: 'ERRORS.ApplicationError.MyAirSupport',
          callback: () => {
            this.router.navigate(['/support']);
          },
          textAfterLink: 'ERRORS.ApplicationError.ProblemContinues',
        },
      ],
    };
    this.messageService.info(errorMessage, errorOptions);
  }

  handleGraphQlErrors(gqlErrors: GraphQLError[]): void {
    gqlErrors.forEach((gqlError) => {
      const errorType = gqlError.errorInfo?.errorType;
      switch (errorType) {
        case ErrorTypes.unauthorized:
        case ErrorTypes.forbidden:
          this.router.navigate(['/']);
          break;
        case ErrorTypes.badRequest:
          this.handleBadRequestErrors(gqlError);
          break;
        default:
          this.logError(gqlError.message);
          this.showErrorMessage();
      }
    });
  }

  handleBadRequestErrors(gqlError: GraphQLError): void {
    this.logGraphQlError(gqlError);
    switch (gqlError.errorInfo.errorCode) {
      case ErrorCodes.policyNotAccepted:
        this.router.navigate(['/accept-policies']);
        break;
      case ErrorCodes.unsupportedPlatform:
        if (!this.isNotSupportedModalOpen) {
          this.isNotSupportedModalOpen = true;
          const messageKey =
            gqlError.errorInfo?.additionalInfo?.patientJourney === 'therapyRC'
              ? 'REGISTRATION-FORM.onboarding_respiratory_machines_not_supported'
              : undefined;
          const dialogRef = this.dialog.open(NotSupportedComponent, { data: { messageKey } });
          dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(() => {
              this.isNotSupportedModalOpen = false;
            });
        }
        break;
      case ErrorCodes.missingPatientJourneys:
        // Store missingPatientJourneys userInfo
        if (gqlError.errorInfo?.additionalInfo?.userInfo) {
          this.localStorageService.setItem(
            'userInfo',
            JSON.stringify(gqlError.errorInfo.additionalInfo.userInfo),
          );
        }
        // open Experience Launcher
        this.router.navigate(['/experience-launcher']);
        break;
      // Silently handle onboardingFlowInProgress, as it's used to manage in-progress flows internally.
      // No user action needed; UI redirects and logic depend on flow details in the error payload.
      case ErrorCodes.onboardingFlowInProgress:
        break;
      default:
        // Handle any other bad request errors here
        this.showErrorMessage();
        break;
    }
  }

  logGraphQlError(gqlError: GraphQLError): void {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.error(
        `GraphQL ${gqlError.errorInfo.errorType} ERROR: ${gqlError.errorInfo.errorCode}`,
        gqlError,
      );
    }
  }

  logError(message: string): void {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.error(message);
    }
  }
}
